import React from "react";
import { Link } from "react-router-dom";
import {
  PRIVACY_POLICY,
  COOKIE_POLICY,
  TERMS_OF_USE,
  LEGAL_LAST_UPDATED,
  COMPANY,
} from "@/lib/legal";

const TITLES = {
  privacy: "Privacy Policy",
  cookies: "Cookie Policy",
  terms: "Terms of Use",
};

const CONTENT = {
  privacy: PRIVACY_POLICY,
  cookies: COOKIE_POLICY,
  terms: TERMS_OF_USE,
};

/**
 * Generic legal-page renderer. The content lives in `/app/frontend/src/lib/legal.js`
 * as plain-text constants. When profitmind.co.uk launches, the same text can be
 * lifted straight into a static HTML page on that site and the footer links here
 * swapped to point at the external URLs.
 */
export default function LegalPage({ kind }) {
  const title = TITLES[kind] || "Legal";
  const body = CONTENT[kind] || "";

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Slim header — kept independent of AppShell so the page works on
          both the unauthenticated landing route and inside the logged-in app. */}
      <header className="border-b border-black/10">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-heading uppercase text-lg tracking-wide"
            data-testid="legal-home-link"
          >
            forecastly
          </Link>
          <Link
            to="/"
            className="text-sm font-semibold underline-offset-4 hover:underline"
          >
            ← Back home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-16">
        <div className="section-eyebrow" data-testid="legal-eyebrow">
          Legal · {title.toLowerCase()}
        </div>
        <h1
          className="font-heading uppercase text-3xl md:text-4xl mt-3 tracking-wide"
          data-testid="legal-title"
        >
          {title}
        </h1>
        <p className="text-sm text-black/55 mt-2">
          Last updated: {LEGAL_LAST_UPDATED}. {COMPANY.legal_name} (trading as{" "}
          {COMPANY.trading_name}).
        </p>

        {/* Content. Plain-text constants are rendered with `whitespace-pre-wrap` so
            paragraph breaks and section numbers stay clean without us needing a
            full Markdown renderer. */}
        <article
          className="mt-8 leading-relaxed text-black/85 whitespace-pre-wrap text-[15px]"
          data-testid={`legal-body-${kind}`}
        >
          {body}
        </article>

        {/* Cross-links between the 3 legal pages */}
        <nav className="mt-12 pt-6 border-t border-black/15 flex flex-wrap gap-4 text-sm font-semibold">
          {Object.entries(TITLES).map(([k, label]) =>
            k === kind ? null : (
              <Link
                key={k}
                to={`/${k}`}
                className="underline-offset-4 hover:underline"
                data-testid={`legal-link-${k}`}
              >
                {label} →
              </Link>
            ),
          )}
        </nav>

        <footer className="mt-10 pt-6 border-t border-black/15 text-xs text-black/55 leading-relaxed">
          <p>
            <strong>{COMPANY.legal_name}</strong>
            <br />
            {COMPANY.address}
            <br />
            <a href={`mailto:${COMPANY.email}`} className="underline">
              {COMPANY.email}
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
}
